
import { useState, useEffect, useContext } from 'react';

// Contexts

import { getContext, getInitialValue } from '../contexts/documentsVerificationContexts';

// Utils

import { getVerificationDocumentsConfig } from '../utils/miscellaneous/getVerificationDocumentsConfig';
import { preventerTools } from '../utils/tools/preventerTools';

// Constants

import { UPLOADED_DOCUMENT_STATUSES } from '../constants/common/miscellaneous';


export const useDocumentVerificationMockDataUpdate = (initialMockData, updateMockDataCallback) => {

    const DEFAULT_STATE = {
        applicants: [],
        caseNumber: ''
    };

    const [ , actionInfoUpdater ] = useContext(getContext('fileInfo'));

    const [ mockData, updateData ] = useState(preventerTools.provideObject(initialMockData, DEFAULT_STATE));

    const updateMockData = (...args) => {
        const [
            actionType,
            payload,
            stateCode,
            applicantGuid,
            applicantEventId
        ] = args;

        switch(actionType) {
            case 'clear':
                updateData(DEFAULT_STATE);
                break;

            case 'inject':
                updateData(payload);
                actionInfoUpdater(getInitialValue('fileInfo'));
                break;

            case 'processTicket':
                {
                    const {
                        applicantGuid,
                        categoryType,
                        documentStatus,
                        uploadedFileName
                    } = payload;

                    const updatedData = {
                        ...mockData,
                        applicants: mockData.applicants.map(applicant => {
                            return applicant.applicantGuid === applicantGuid ?
                                {
                                    ...applicant,
                                    validationCompleted: documentStatus === UPLOADED_DOCUMENT_STATUSES.VERIFIED,
                                    documents: applicant.documents.map(category => {
                                        return category.categoryType === categoryType ?
                                            {
                                                ...category,
                                                documentTypes: category.documentTypes.map(dt => {
                                                    return dt.uploadedFileName === uploadedFileName ?
                                                        {
                                                            ...dt,
                                                            documentStatus,
                                                            ticket: null,
                                                            encryptedId: null
                                                        } :
                                                        dt;
                                                })
                                            } :
                                            category;
                                    })
                                } :
                                applicant;
                        })
                    };

                    updateData(updatedData);
                }
                break;

            case 'remove':
                {
                    const {
                        categoryType,
                        documentType
                    } = payload;

                    const updatedData = {
                        ...mockData,
                        applicants: mockData.applicants.map(applicant => {
                            
                            return applicant.applicantGuid === applicantGuid ?
                                {
                                    ...applicant,
                                    documents: 
                                        applicant
                                            .documents
                                            .reduce((categories, category) => {

                                                const filteredDocuments = (
                                                    category.documentTypes
                                                        ? category.documentTypes : []
                                                ).filter(doc => doc.documentType !== documentType || (doc.documentType === documentType && doc.documentStatus !== UPLOADED_DOCUMENT_STATUSES.INITIAL));

                                                return category.categoryType === categoryType ?
                                                    [
                                                        ...categories,
                                                        ...(
                                                            filteredDocuments.length ?
                                                                {
                                                                    ...category,
                                                                    documentTypes: filteredDocuments
                                                                }:
                                                                []
                                                        )
                                                    ] :
                                                    [
                                                        ...categories,
                                                        category
                                                    ];
                                            }, [ ])
                                } :
                                applicant;
                        })
                    };

                    actionInfoUpdater({
                        applicantEventId,
                        caseNumber: mockData.caseNumber,
                        categoryType,
                        documentType
                    });

                    updateData(updatedData);
                }
                break;

            case 'override':
                {
                    const [
                        applicantEventId,
                        categoryType,
                    ]  = payload;


                    const checkIfApplicantMatch = applicant => applicant.applicantEventId === applicantEventId;
                    const checkIfCategoryMatch = category => category.categoryType === categoryType;

                    const updatedData = {
                        ...mockData,
                        applicants: mockData.applicants.map(applicant => (
                            {
                                ...applicant,
                                documents: applicant.documents.reduce(
                                    (documents, category) => {
                                        return [
                                            ...documents,
                                            ...(
                                                checkIfApplicantMatch(applicant) && checkIfCategoryMatch(category) ?
                                                    {
                                                        ...category,
                                                        overrideDone: true
                                                    } :
                                                    category
                                            )
                                        ];
                                    },
                                    checkIfApplicantMatch(applicant) && !applicant.documents.some(category => checkIfCategoryMatch(category))
                                        ? [{ categoryType, overrideDone: true, documentTypes: [] }] : []
                                )
                            }
                        ))
                    };

                    updateData(updatedData);

                }
                break;
            case 'submit':
                {
                    const [
                        applicantEventId
                    ]  = payload;

                    let ticketCounter = 9;
                    let encryptedIdCounter = 959;

                    const updatedData = {
                        ...mockData,
                        applicants: mockData.applicants.map(applicant => (
                            {
                                ...applicant,
                                ...(
                                    applicant.applicantEventId === applicantEventId ?
                                        {
                                            validationCompleted: true,
                                            documents: applicant.documents.map(category => {
                                                return {
                                                    ...category,
                                                    documentTypes: category.documentTypes.map(dt => {
                                                        return {
                                                            ...dt,
                                                            ...(
                                                                dt.documentStatus === UPLOADED_DOCUMENT_STATUSES.INITIAL
                                                                    ? {
                                                                        ticket: ++ticketCounter,
                                                                        encryptedId: ++encryptedIdCounter,
                                                                        documentStatus: UPLOADED_DOCUMENT_STATUSES.SUBMITTED

                                                                    } : {}
                                                            )
                                                        };
                                                    })
                                                };
                                            })
                                        } :
                                        {}
                                ),
                            }
                        ))
                    };

                    updateData(updatedData);
                }
                break;

            case 'upload':
                {
                    const extractedProps = [
                        'applicantEventId',
                        'caseNumber',
                        'categoryType',
                        'dataType',
                        'documentType',
                        'name',
                        'file',
                        'size'
                    ];

                    const fileInfo = extractedProps.reduce((res, prop) => {
                        return {
                            ...res,
                            [ prop ]: payload.get(prop)
                        };
                    }, {});

                    const {
                        categoryType,
                        documentType,
                        name: uploadedFileName,
                        size: uploadedFileSize,
                        file
                    } = fileInfo;

                    actionInfoUpdater(fileInfo);

                    const getDocuments = applicant => {

                        const {
                            event,
                            documents
                        } = applicant;

                        return getVerificationDocumentsConfig(stateCode, event).documents.reduce((categories, category) => {
                            
                            const matchingCategory = documents.find(cat => cat.categoryType === category.categoryType);

                            if(category.categoryType === categoryType) {
                                
                                const uploadedDocument = {
                                    documentType,
                                    uploadedFileName,
                                    uploadedFileSize,
                                    documentStatus: UPLOADED_DOCUMENT_STATUSES.INITIAL,
                                    encTicketId: null,
                                    ticket: null,
                                    encryptedDocumentPath: URL.createObjectURL(file)
                                };
                                
                                return matchingCategory ?
                                    [
                                        ...categories,
                                        {
                                            ...matchingCategory,
                                            documentTypes: [
                                                ...matchingCategory.documentTypes
                                                    .filter(
                                                        dt => !(dt.documentType === documentType &&
                                                            dt.documentStatus === UPLOADED_DOCUMENT_STATUSES.INITIAL)),
                                                uploadedDocument
                                            ]
                                        }
                                    ] :
                                    [
                                        ...categories,
                                        {
                                            categoryType,
                                            documentTypes: [
                                                uploadedDocument
                                            ]
                                        }
                                    ];
                            } else {
                                return matchingCategory ?
                                    [
                                        ...categories,
                                        matchingCategory
                                    ] :
                                    categories;
                            }
                        }, [ ]);
                    };

                    const updatedData = {
                        ...mockData,
                        applicants: mockData.applicants.map(applicant => {
                            return applicant.applicantGuid === applicantGuid ? 
                                {
                                    ...applicant,
                                    documents: getDocuments(applicant)
                                } :
                                applicant;
                        })
                    };

                    updateData(updatedData);
                }
                break;
            default:
                return;
        }
    };

    useEffect(() => {
        updateData(initialMockData);
    }, [ JSON.stringify(initialMockData) ]);

    useEffect(() => {
        updateMockDataCallback(mockData);
    }, [ mockData ]);

    return [ updateMockData ];
};